<template>
  <div class="financial-plan-table table">
    <div class="thead">
      <div class="tr">
        <div class="th !flex-[3.5] !pt-0 !align-start">
          <h3 class="!text-[11pt] m-0">
            <span class="capitalize">{{ budgetType }}</span> Budget (R'000)
          </h3>
        </div>

        <div
          v-for="year in years"
          :key="year.key"
          class="th"
        >
          {{ year.label }}
        </div>
      </div>
    </div>

    <div class="tbody">
      <div
        v-for="strategy in strategies"
        :key="strategy.id"
        class="tr"
      >
        <div class="td !flex-[3.5]">
          {{ strategy.name }}
        </div>

        <div
          v-for="year in years"
          :key="year.key"
          class="td"
        >
          {{ formatValue(getValue(strategy.id, year.key)) }}
        </div>
      </div>
    </div>

    <!-- This ensures this row is always included even when there are no development strategies present -->
    <div class="tbody">
      <div class="tr">
        <div class="td !flex-[3.5] font-semibold">
          Other expenditure
        </div>
        <div
          v-for="year in years"
          :key="year.key"
          class="td"
        >
          {{ formatValue(plan.content.financialPlan?.otherExpenditure?.[budgetType]?.[year.key] ?? 0) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStringFormatter } from '@/composables';
import { mtrefYearHeadings } from '@/const';

const stringFormatter = useStringFormatter();

const props = defineProps({
  plan: {
    type: Object,
    required: true,
  },
  budgetType: {
    type: String,
    required: true,
  },
});

// Both tables cover the three MTREF years, stored under positional year keys.
const years = mtrefYearHeadings.map((label, index) => ({ key: `year${index + 1}`, label }));

// Development strategies drive the rows of both budget tables.
const strategies = computed(() => props.plan.content.developmentStrategies || []);

// Capital budgets remain calculated by summing the capital budget of every project
// linked to each development strategy. Operating budgets are captured manually.
const capitalTotals = computed(() => {
  const totals = {};

  strategies.value.forEach(strategy => {
    totals[strategy.id] = Object.fromEntries(years.map(({ key }) => [key, 0]));
  });

  const sections = props.plan.content.implementationPlan?.municipalProgrammesAndProjects || [];

  sections.forEach(section => {
    (section.projects || []).forEach(project => {
      const entry = totals[project.developmentStrategyId];

      if (entry && project.budget) {
        years.forEach(({ key }) => {
          entry[key] += parseFloat(project.budget.capital?.[key]) || 0;
        });
      }
    });
  });

  return totals;
});

const getValue = (strategyId, yearKey) => {
  // Operating totals are read from the manually-entered field; capital stays calculated.
  if (props.budgetType === 'operating') {
    const entry = props.plan.content.financialPlan?.operatingBudgetByStrategy
      ?.find(item => item.developmentStrategyId === strategyId);

    return entry?.[yearKey] ?? 0;
  }

  return capitalTotals.value[strategyId]?.[yearKey] ?? 0;
};

const formatValue = (value) => {
  const formatted = stringFormatter.financialTableFormatters['currency'].format(value);
  // Zero and empty cells display as a dash in the finalised plan
  return formatted === '' || formatted === '0' ? '-' : formatted;
};
</script>
